import Button from "antd/es/button"
import Popover from "antd/es/popover"
import Select from "antd/es/select"
import Space from "antd/es/space"
import Tag from "antd/es/tag"
import { FilterIcon } from "lucide-react"
import { useMemo } from "react"

import type {
  FeedFilterState,
  UseFeedFiltersResult,
} from "../../hooks/useQuestionnaireFeedFilters"
import {
  type MimeType,
  QUESTIONNAIRE_ASSISTANT_SHEET_MIMETYPES,
} from "../../types/mimetype"
import { pluralize, unique, uniqueByField } from "../../utils"
import type { AnswerWithDiscussion } from "./types"

interface Props {
  answers: AnswerWithDiscussion[]
  filterState: FeedFilterState
  setFilterValue: UseFeedFiltersResult["setFilterValue"]
  resetFilters: UseFeedFiltersResult["resetFilters"]
  activeFilterCount: number
  jobMimeType: MimeType | null
}

const FeedFilterPopover: React.FC<Props> = ({
  filterState,
  setFilterValue,
  resetFilters,
  answers,
  activeFilterCount,
  jobMimeType,
}) => {
  const handleFilterChange =
    (key: keyof FeedFilterState) =>
    (value: string[] | string | null | undefined) => {
      setFilterValue(key, value)
    }
  const { owners, assignees, sections } = useMemo(
    () => ({
      owners: uniqueByField(
        answers.map((item) => item.last_assigned_to).filter((item) => !!item),
        "uid",
      ),
      assignees: uniqueByField(
        answers
          .flatMap((item) => item.discussions ?? [])
          .filter((discussion) => discussion.assignment)
          .map((discussion) => discussion.assignment!),
        "uid",
      ),
      sections: unique(
        answers
          .map((item) => item.primary_question)
          .filter((item) => item.location)
          .map((item) => item.location?.sheetName || ""),
      ),
    }),
    [answers],
  )

  const filterContent = (
    <Space direction="vertical" className="w-60">
      {filterState.oid.length > 0 && (
        <Tag closable onClose={() => setFilterValue("oid", [])}>
          {filterState.oid.length} {pluralize(filterState.oid.length, "answer")}
        </Tag>
      )}

      <div className="mt-2 font-semibold">Approval Status</div>
      <Select
        rootClassName="w-full"
        placeholder="Select approval status"
        value={filterState.approved}
        onChange={handleFilterChange("approved")}
        allowClear
        options={[
          { value: "1", label: "Approved" },
          { value: "0", label: "Not Approved" },
        ]}
      />

      <div className="mt-2 font-semibold">Assigned To</div>
      <Select
        mode="multiple"
        rootClassName="w-full"
        placeholder="Filter by assignee"
        value={filterState.discussionAssignedTo}
        onChange={handleFilterChange("discussionAssignedTo")}
        options={assignees.map((assignee) => ({
          value: assignee.uid,
          label: assignee.email,
        }))}
      />

      <div className="mt-2 font-semibold">Quilt Answered</div>
      <Select
        rootClassName="w-full"
        placeholder="Did Quilt answer?"
        value={filterState.confidence}
        onChange={handleFilterChange("confidence")}
        allowClear
        options={[
          { value: "1", label: "Quilt Answered" },
          { value: "0", label: "Quilt Did Not Answer" },
        ]}
      />

      <div className="mt-2 font-semibold">Owner</div>
      <Select
        mode="multiple"
        rootClassName="w-full"
        placeholder="Filter by owner"
        value={filterState.lastAssignedTo}
        onChange={handleFilterChange("lastAssignedTo")}
        options={owners.map((assignee) => ({
          value: assignee.uid,
          label: assignee.email,
        }))}
      />

      {(!jobMimeType ||
        QUESTIONNAIRE_ASSISTANT_SHEET_MIMETYPES.includes(jobMimeType)) && (
        <>
          <div className="mt-2 font-semibold">Sheet Name</div>
          <Select
            mode="multiple"
            rootClassName="w-full"
            placeholder="Filter by sheet name"
            value={filterState.section}
            onChange={handleFilterChange("section")}
            options={sections.map((section) => ({
              value: section,
              label: section,
            }))}
          />
        </>
      )}

      <Button
        className="mt-4"
        onClick={resetFilters}
        disabled={activeFilterCount === 0}
      >
        Clear All Filters
      </Button>
    </Space>
  )

  return (
    <Popover
      content={filterContent}
      title="Filter Options"
      trigger="click"
      placement="bottomLeft"
    >
      <Button
        icon={<FilterIcon size="1em" />}
        type={activeFilterCount > 0 ? "primary" : "default"}
      >
        {activeFilterCount > 0
          ? `${activeFilterCount} ${pluralize(activeFilterCount, "Filter")}`
          : "Filter"}
      </Button>
    </Popover>
  )
}

export default FeedFilterPopover
